import { nextUnit, type DropsetStepDraft } from '@heraion/shared';
import { convertWeightText } from '../../convertWeightText';
import {
  blankSetFor,
  bornExtraNegative,
  failToggleFields,
  initial,
  insertAt,
  removeAt,
  replaceAt,
  seedInsertedSet,
  seedNextSet,
  completeAssisted,
  withBornSign,
  withEntry,
  withSet,
} from './loggerDraftHelpers';
import type { LoggerAction, LoggerDraft } from './loggerDraftTypes';
import { noteEventReducer } from './noteEventReducer';
import { loggerGroupReducer } from './loggerGroupReducer';
import { calibrationReducer } from './calibrationReducer';
import { moreTimeReducer } from './moreTimeReducer';
import { loggerSessionReducer } from './loggerSessionReducer';
import { loggerSwapReducer } from './loggerSwapReducer';
import { hydrateDraft } from './hydrateDraft';

// The nested-draft reducer: every Logger action expressed against the
// hierarchical LoggerDraft (entries[].sets[]). This is the battle-tested
// logic the live flat store reuses verbatim — loggerDraft.ts wraps it with
// flattenDraft/assembleDraftKeepIds so a flat WorkoutState reduces through
// these exact cases (see loggerDraft.ts flatReducer). Extracted from
// loggerDraft.ts when the flat store pushed that file over 500 lines.

export function loggerReducer(
  state: LoggerDraft,
  action: LoggerAction,
): LoggerDraft {
  // Timeline note events live on a separate slice (state.noteEvents) and never
  // touch entries[].sets[]; delegate them so this switch stays focused on the
  // set/entry model. The early return narrows `action` so the switch below
  // stays exhaustive over the remaining union (TS discriminated-union narrowing).
  if (
    action.type === 'add_note_event' ||
    action.type === 'update_note_event' ||
    action.type === 'remove_note_event'
  ) {
    return noteEventReducer(state, action);
  }
  // Superset/circuit GROUP actions go the same way, for the same reason and
  // by the same mechanism (EQ-1, 2026-08-13, when this file crossed 500):
  // each of them keys on `entry.group?.id` and reasons about the group as a
  // whole, while the switch below reasons about one entry or one set. That
  // line already existed; the delegation only makes it a file boundary.
  // Spelled out rather than tested against a Set of names: a Set lookup is
  // opaque to TS, which would leave the switch below non-exhaustive and
  // force a cast here — and a cast is exactly where a missed action would
  // hide. This disjunction narrows, so both halves stay checked.
  if (
    action.type === 'add_round_to_group' ||
    action.type === 'remove_round_from_group' ||
    action.type === 'align_group_sets' ||
    action.type === 'set_group' ||
    action.type === 'split_group' ||
    action.type === 'remove_group_member' ||
    action.type === 'move_group_member' ||
    action.type === 'toggle_group_effort'
  ) {
    return loggerGroupReducer(state, action);
  }
  // #353: the MAX calibration, same mechanism.
  if (action.type === 'answer_calibration' || action.type === 'append_calibration_set') {
    return calibrationReducer(state, action);
  }
  // The SESSION's own fields go the same way, for the same reason (LG-77,
  // 2026-09-08, when this file was found 16 lines over the cap): each of them
  // writes a field of the workout itself, while the switch below reasons about
  // one entry or one set. Spelled out rather than tested against a Set, like
  // the two above: a disjunction narrows, a Set lookup would force a cast.
  if (
    action.type === 'set_name' ||
    action.type === 'set_location' ||
    action.type === 'set_started_at' ||
    action.type === 'set_ended_at' ||
    action.type === 'start_today_draft'
  ) {
    return loggerSessionReducer(state, action);
  }
  // #355 the exercise swap and #356 «More time», same mechanism.
  if (action.type === 'replace_exercise') return loggerSwapReducer(state, action);
  if (action.type === 'add_more_time') return moreTimeReducer(state, action);
  switch (action.type) {
    case 'add_exercise':
      return {
        ...state,
        entries: [
          ...state.entries,
          {
            exerciseId: action.exerciseId,
            exerciseName: action.exerciseName,
            isBodyweight: action.isBodyweight,
            isAssisted: action.isAssisted,
            movementPattern: action.movementPattern,
            group: null,
            // EQ-1: already resolved by the caller, or absent. The reducer
            // never decides which machine — it only records the answer.
            equipmentInstance: action.equipmentInstance ?? null,
            location: action.location ?? null,
            // Default to bilateral (multiplier 1): the entered weight is
            // the total load. LoggerScreen seeds this to 2 (unilateral)
            // for exercises flagged Exercise.unilateral, and the
            // entry-header chip flips it as a manual override (sticky
            // preference seeded from AsyncStorage at the LoggerScreen
            // layer).
            loadedSides: 1,
            isCardio: action.isCardio,
            isTimeBased: action.isTimeBased,
            // Effort tracking ON by default for everything EXCEPT cardio and
            // timed holds (opt-out per entry). Bodyweight included: RIR
            // ("reps left in the tank") is just as meaningful on pull-ups.
            effortColumn: !action.isCardio && !action.isTimeBased,
            historyOpen: false,
            // #213: born with the catalog's sign (an assisted lift starts on).
            sets: [blankSetFor(action, action.defaultUnit, 1)],
          },
        ],
      };
    case 'remove_exercise':
      return {
        ...state,
        entries: removeAt(state.entries, action.entryIdx),
      };
    case 'refresh_carried_weights': {
      const entry = state.entries[action.entryIdx];
      if (
        !entry ||
        entry.exerciseId !== action.exerciseId ||
        entry.sets.length !== action.expected.length
      ) {
        return state;
      }
      // Only a cell still exactly as the swap left it takes the late answer.
      let changed = false;
      const sets = entry.sets.map((s, i) => {
        const next = action.carriedWeights[i];
        if (!next || s.completedAt !== null) return s;
        const was = action.expected[i];
        const intact = was
          ? s.weight === was.weight && s.weightSource === was.source
          : s.weight === '' && s.weightSource === undefined;
        if (!intact) return s;
        changed = true;
        return {
          ...s,
          weight: next.weight,
          weightSource: next.source,
          weightFellBack: undefined,
          // A carried weight is a weighted history/MAX number: positive.
          extraNegative: bornExtraNegative(entry, { weight: next.weight, extraNegative: false }),
        };
      });
      if (!changed) return state;
      return withEntry(state, action.entryIdx, (e) => ({ ...e, sets }));
    }
    case 'add_set':
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        sets: [...entry.sets, seedNextSet(entry, action.defaultUnit)],
      }));
    case 'remove_set':
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        sets: removeAt(entry.sets, action.setIdx),
      }));
    case 'insert_set_at':
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        sets: insertAt(
          entry.sets,
          action.setIdx,
          seedInsertedSet(entry, action.setIdx, action.defaultUnit),
        ),
      }));
    case 'update_set':
      return withSet(state, action.entryIdx, action.setIdx, (set) => {
        // Flip provenance to 'manual' the moment the athlete touches
        // the weight cell — even if the new string happens to match
        // the prefilled value. The intent (athlete confirmed it) is
        // the distinction, not the bytes (Phase 1, default-weight chain).
        const sourceField =
          'weight' in action.patch
            ? { weightSource: 'manual' as const, weightFellBack: undefined }
            : {};
        return {
          ...set,
          ...action.patch,
          ...sourceField,
        };
      });
    case 'toggle_unit':
      // ⛔ Il chip CONVERTE, non ri-etichetta (LG-48). Il perche' sta tutto in
      // convertWeightText. Qui la sola cosa locale: i passi del dropset non
      // hanno un'unita' propria, vivono in quella del set che li porta.
      return withSet(state, action.entryIdx, action.setIdx, (set) => {
        // CN-40 — `nextUnit`, not the ternary. Third and last copy.
        const to = nextUnit(set.unit);
        return {
          ...set,
          unit: to,
          weight: convertWeightText(set.weight, set.unit, to),
          ...(set.dropset === null
            ? {}
            : {
                dropset: {
                  ...set.dropset,
                  steps: set.dropset.steps.map((step) => ({
                    ...step,
                    weight: convertWeightText(step.weight, set.unit, to),
                  })),
                },
              }),
        };
      });
    case 'toggle_sign':
      return withSet(state, action.entryIdx, action.setIdx, (set) => ({
        ...set,
        extraNegative: !set.extraNegative,
      }));
    case 'cycle_set':
      // pending → done | pr → failed → pending. Same cycle the web's
      // useSetCompletion drives. The PR check (isSetPR) runs at the
      // dispatch site so prFlash arrives precomputed on the
      // pending→done transition; rest timer + audio cue stay
      // out-of-scope for the mobile MVP. The fail leg and the
      // unwind-to-pending leg both clear prFlash so the gold pill
      // doesn't linger after the user reverses a record.
      return withSet(state, action.entryIdx, action.setIdx, (set) => {
        if (set.completedAt === null) {
          return {
            ...set,
            // Live logging → wall clock. Past-session edit → the
            // dispatch site precomputes an in-window time (see
            // useSetCyclePill + timelinePlacement.ts).
            completedAt: action.completedAt ?? new Date().toISOString(),
            fail: false,
            prFlash: action.prFlash === true,
            regressionFlash: action.regressionFlash === true,
          };
        }
        if (!set.fail) {
          // done → failed. `fail` is a pure label (AP-0p model); the rep
          // decrement is a separate step done by the fail-confirm popup
          // (dispatch site), not here.
          return { ...set, ...failToggleFields(true) };
        }
        return { ...set, completedAt: null, ...failToggleFields(false) };
      });
    case 'uncomplete_set':
      // LG-67 — the timeline row's ✕. Straight to pending in ONE transition,
      // never through `failed` the way a doubled `cycle_set` would (the reason
      // lives on the action type). Values are kept on purpose: the athlete is
      // moving a set to the right exercise, not throwing the numbers away.
      // A pending set is a no-op rather than an error — the ✕ only ever renders
      // on a logged row, so reaching here twice means a double tap, not a bug.
      return withSet(state, action.entryIdx, action.setIdx, (set) =>
        set.completedAt === null
          ? set
          : { ...set, completedAt: null, ...failToggleFields(false) },
      );
    case 'cycle_dropset_step': {
      const dropset = state.entries[action.entryIdx]?.sets[action.setIdx]
        ?.dropset;
      if (!dropset) return state;
      const step = dropset.steps[action.stepIdx];
      if (!step) return state;
      const nextStep: DropsetStepDraft = {
        ...step,
        completedAt:
          step.completedAt === null ? new Date().toISOString() : null,
      };
      const steps = replaceAt(dropset.steps, action.stepIdx, nextStep);
      return withSet(state, action.entryIdx, action.setIdx, (set) => ({
        ...set,
        dropset: { ...dropset, steps },
      }));
    }
    // Single effort column (RIR | RPE per Profile.effortScale; the two
    // are the same quantity, RIR = 10 − RPE). Per-set values on
    // DraftSet.rpe / DraftSet.rir survive the column flip so re-toggling
    // restores them.
    case 'toggle_entry_effort':
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        effortColumn: action.next,
      }));
    // Group-level effort toggle (single chip on the group card, 2026-05-28).
    // Flips effortColumn for every non-cardio member of the group together,
    // so a superset can't end up with RIR on one leg and not the other.
    // Cardio members are left untouched (no per-side effort to track).
    case 'toggle_entry_history':
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        historyOpen: action.next,
      }));
    case 'set_entry_loaded_sides': {
      const nextSides = action.loadedSides;
      // Restamp pending sets so the live volume math reflects the new
      // mode immediately. Completed/failed sets keep their stamped
      // value; the user's history of what they actually lifted stays
      // honest.
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        loadedSides: nextSides,
        sets: entry.sets.map((s) =>
          s.completedAt === null ? { ...s, loadedSides: nextSides } : s,
        ),
      }));
    }
    case 'copy_history_to_entry':
      if (action.sets.length === 0) return state;
      // Universal fill rule (slice #2, 2026-05-28): position-fill only,
      // never extend. For each target position i, replace with source[i]
      // when target[i] is pending; completed/failed sets are protected;
      // trailing positions beyond min(N,M) stay untouched. Growing the
      // entry is the job of add_set / +Add round, not copy.
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        sets: entry.sets.map((targetSet, i) => {
          const sourceSet = action.sets[i];
          if (!sourceSet) return targetSet;
          if (targetSet.completedAt !== null || targetSet.fail) return targetSet;
          return withBornSign(entry, sourceSet);
        }),
      }));
    case 'seed_new_entry_sets':
      // Replace the fresh entry's seed sets wholesale with the prefill
      // payload (was the `mode: 'overwrite'` short-circuit on a blank
      // entry; split out in slice #2 so copy stays purely position-fill).
      if (action.sets.length === 0) return state;
      return withEntry(state, action.entryIdx, (entry) => ({
        ...entry,
        sets: action.sets.map((s) => withBornSign(entry, s)),
      }));
    case 'hydrate':
      // Legacy-draft backfill lives in its own module (hydrateDraft.ts);
      // it's the single chokepoint for AsyncStorage rehydration + the
      // edit/repeat workflows, unrelated to the set/entry action switch.
      return hydrateDraft(action.draft);
    case 'complete_assisted':
      return completeAssisted(state, action.catalog);
    case 'reset':
      return initial();
  }
}
